/**
 * Mode « détails techniques » — et, depuis la v0.5.0 (B-604, ART-0175 § 7), le REGISTRE DE
 * VOCABULAIRE : métier (défaut des profils métier et PO) ou technique (défaut des profils
 * développeur, testeur et release manager), à un clic l'un de l'autre.
 *
 * Public métier : la plomberie (chemins de fichiers, IDs de cartes, noms de modèles,
 * empreintes, JSON) est MASQUÉE au profit de titres humains et des mots du métier ;
 * l'activer la fait réapparaître. Calqué sur `theme.ts` + LanguageProvider : un attribut
 * `data-technical` sur <html> (masquage CSS des fuites décoratives) ET un contexte réactif
 * (retrait de colonnes/blocs entiers, choix des mots).
 *
 * DEUX SOURCES, DANS CET ORDRE : le CHOIX EXPLICITE de la personne (bascule, persisté) puis
 * le DÉFAUT DE SON PROFIL (`appliquerDefaut`, posé par la vue d'accueil). Un choix explicite
 * n'est jamais écrasé par un changement de vue.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
  type JSX, type ReactNode,
} from 'react'

const KEY = 'doit-technical'

/** Choix explicite enregistré : `true`/`false`, ou `null` si la personne n'a jamais choisi. */
export function getTechnicalExplicite(): boolean | null {
  try {
    const v = localStorage.getItem(KEY)
    return v === '1' ? true : v === '0' ? false : null
  } catch {
    return null
  }
}

export function getTechnical(): boolean {
  return getTechnicalExplicite() ?? false
}

export function applyTechnical(on: boolean): void {
  const root = document.documentElement
  if (on) root.setAttribute('data-technical', '')
  else root.removeAttribute('data-technical')
}

/** Applique le réglage stocké au démarrage, avant le premier rendu. */
export function initTechnical(): void {
  applyTechnical(getTechnical())
}

interface TechnicalValue {
  technical: boolean
  /** Choix explicite de la personne (bascule) — persisté, prioritaire sur le profil. */
  setTechnical: (on: boolean) => void
  /** Défaut du profil : appliqué seulement tant qu'aucun choix explicite n'existe. */
  appliquerDefaut: (on: boolean) => void
  /** Vrai si l'état courant vient d'un choix explicite. */
  explicite: boolean
}

const Ctx = createContext<TechnicalValue | null>(null)

export function TechnicalProvider({ children }: { children: ReactNode }): JSX.Element {
  const [etat, setEtat] = useState<{ technical: boolean; explicite: boolean }>(() => {
    const choix = getTechnicalExplicite()
    return { technical: choix ?? false, explicite: choix !== null }
  })
  const setTechnical = useCallback((on: boolean) => {
    setEtat({ technical: on, explicite: true })
    try { localStorage.setItem(KEY, on ? '1' : '0') } catch { /* stockage indisponible */ }
    applyTechnical(on)
  }, [])
  const appliquerDefaut = useCallback((on: boolean) => {
    setEtat((avant) => {
      if (avant.explicite || avant.technical === on) return avant
      applyTechnical(on)
      return { technical: on, explicite: false }
    })
  }, [])
  const value = useMemo(() => ({ ...etat, setTechnical, appliquerDefaut }), [etat, setTechnical, appliquerDefaut])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Hook : `const { technical, setTechnical } = useTechnical()`. */
export function useTechnical(): TechnicalValue {
  const v = useContext(Ctx)
  if (!v) return { technical: false, setTechnical: () => {}, appliquerDefaut: () => {}, explicite: false }
  return v
}
